// ─────────────────────────────────────────────────────────────────────────────
// mapsProvider.ts — the Google Maps JavaScript API loader, its credential, and
// the dark style the map is drawn in.
//
// ── THE MAP MOVES LOCALLY ────────────────────────────────────────────────────
// Panning and zooming are the browser's own. Nothing in this file or its consumer
// subscribes to `idle`, `bounds_changed`, `center_changed` or `zoom_changed`, and
// nothing fetches on view change. Dragging the map is not an event in Rexy: no
// state changed, nothing happened, nothing to record. A "search this area" button
// or a marker click WOULD be an action and would go to the box like every other
// button — neither is wired here, and neither is faked. See DiscoveryMap.tsx.
//
// ── PRESENTATION ONLY ────────────────────────────────────────────────────────
// T1 doctrine, verbatim: "Maps presentation data does not itself equal
// verification/qualification." The canonical record is the T2 row written by
// server/growth_places.py. This draws that record and decides nothing about it —
// no scoring, no qualification, no write path, not even a read of its own. The
// pins arrive as props from what the panel already reads.
//
// ── THE KEY IS A BROWSER KEY AND IT IS PUBLIC BY NATURE ──────────────────────
// It ships in the bundle, visible in page source. That is normal and it is why a
// browser key is defended by REFERRER RESTRICTION rather than by secrecy.
//
// IT IS NOT — AND MUST NEVER BECOME — THE SERVER KEY. That one lives 0600 under
// server/connector_secrets.py, is read only by the box, and calls Places
// server-side under the connector `google_places_growth`. A server key pasted
// into a bundle is a key with no referrer restriction published to every reader
// of the page. The two are separate credentials with separate restrictions, and
// this file can only ever see the browser one: it reads a build-time REACT_APP_
// variable, and the secret store is not reachable from the browser at all.
//
// WHAT THE OWNER MINTS — see docs/GROWTH_MAPS_BROWSER_KEY.md for the full note.
// ─────────────────────────────────────────────────────────────────────────────

import { MC } from './theme';

/** The browser key, inlined by CRA at build time. Empty when none is configured —
 *  which is a NAMED STATE the panel reports, never a silent blank map. */
export const MAPS_BROWSER_KEY: string =
  (process.env.REACT_APP_GOOGLE_MAPS_BROWSER_KEY || '').trim();

/** THE STARTING VIEW, AND IT IS A DECLARED DEFAULT — NOT A CLAIM ABOUT DATA.
 *  A map with no pins has to open somewhere; it opens on the continent the
 *  campaign lanes are scoped to. The moment any pin is mappable the view is fitted
 *  to the pins instead, so this constant is only ever seen over an empty board. */
export const DEFAULT_VIEW = { lat: -25.6, lng: 134.4, zoom: 4 } as const;

/** Zoom used when exactly one pin exists — fitBounds on a single point would
 *  otherwise slam to maximum zoom and show a rooftop with no context. */
export const SINGLE_PIN_ZOOM = 15;

// ── THE DARK STYLE LIVES HERE, IN THE REPO, ON PURPOSE ──────────────────────
// Google's default map is a bright white rectangle and would be the only light
// surface in the product — a review block under the locked dark-theme constraint.
//
// The alternative Google offers is a cloud-console Map ID whose style is edited in
// a browser somewhere else. That would put a load-bearing design token OUTSIDE the
// repo, where no diff shows it changing and no review can block it. It stays here.
//
// Consequence, stated rather than discovered later: `styles` and `mapId` are
// mutually exclusive — a map constructed with a Map ID ignores this array. So this
// map declares NO Map ID, and therefore uses google.maps.Marker rather than
// AdvancedMarkerElement (which requires one). Marker is deprecated as of v3.56 and
// Google states it is NOT scheduled for discontinuation. The upgrade path —
// cloud-styled Map ID + AdvancedMarkerElement — is a real one and it is the
// owner's call, because it moves the palette off the repo.
//
// Colours are the sampled MC tokens. Nothing here was picked by eye.
export const MAP_DARK_STYLE: object[] = [
  { elementType: 'geometry', stylers: [{ color: MC.panel }] },
  { elementType: 'labels.text.fill', stylers: [{ color: MC.muted }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: MC.bg }] },
  { featureType: 'administrative', elementType: 'geometry', stylers: [{ color: MC.line2 }] },
  { featureType: 'administrative.country', elementType: 'labels.text.fill', stylers: [{ color: MC.dim }] },
  { featureType: 'administrative.locality', elementType: 'labels.text.fill', stylers: [{ color: MC.textMuted }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: MC.panel2 }] },
  { featureType: 'road', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: MC.panel3 }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: MC.bg }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: MC.dim }] },
];

// ── LOADER ──────────────────────────────────────────────────────────────────
// ONE script tag per document, ever. The Maps API throws
// "You have included the Google Maps JavaScript API multiple times" and behaves
// unpredictably if injected twice, and a panel that unmounts and remounts on every
// tab change would do exactly that. The promise is module-level, so the second
// caller awaits the first load rather than starting another.

const CALLBACK = '__rexyGrowthMapsReady';
const SCRIPT_ID = 'rexy-growth-maps-js';

/** The minimum of the Maps surface this module touches. Declared locally rather
 *  than adding @types/google.maps — a dependency for four call sites. */
export interface GMapsLatLngBounds { extend(p: { lat: number; lng: number }): void; }
export interface GMapsMap { fitBounds(b: GMapsLatLngBounds, padding?: number): void; setZoom(z: number): void; setCenter(p: { lat: number; lng: number }): void; }
export interface GMapsMarker { setMap(m: GMapsMap | null): void; }
export interface GMapsApi {
  Map: new (el: HTMLElement, opts: Record<string, unknown>) => GMapsMap;
  Marker: new (opts: Record<string, unknown>) => GMapsMarker;
  LatLngBounds: new () => GMapsLatLngBounds;
}

type MapsWindow = Window & {
  google?: { maps?: GMapsApi };
  [CALLBACK]?: () => void;
};

let pending: Promise<GMapsApi> | null = null;

/** Resolves with the Maps API, or REJECTS WITH A REASON A PANEL CAN SHOW.
 *
 *  The three failures are three different things and never share a voice:
 *    • no key configured   — the owner has not minted one yet. Not an error.
 *    • script failed       — network, or the referrer was refused. Transport.
 *    • loaded but no api   — the script ran and left nothing behind.
 *  A key that is REJECTED by Google still loads the script successfully and then
 *  paints its own error over the map; that is Google's message to show, not ours
 *  to intercept, and the console carries the specific code (RefererNotAllowed-
 *  MapError / InvalidKeyMapError). */
export function loadMaps(): Promise<GMapsApi> {
  if (!MAPS_BROWSER_KEY) {
    return Promise.reject(new Error('no-key'));
  }
  const w = window as unknown as MapsWindow;
  if (w.google && w.google.maps) return Promise.resolve(w.google.maps);
  if (pending) return pending;

  pending = new Promise<GMapsApi>((resolve, reject) => {
    w[CALLBACK] = () => {
      const api = w.google && w.google.maps;
      if (api) resolve(api);
      else reject(new Error('loaded-no-api'));
    };
    const s = document.createElement('script');
    s.id = SCRIPT_ID;
    s.async = true;
    // `loading=async` is what Google asks for alongside a callback; `v=weekly` pins
    // the channel rather than floating on whatever "latest" becomes.
    s.src = 'https://maps.googleapis.com/maps/api/js'
      + '?key=' + encodeURIComponent(MAPS_BROWSER_KEY)
      + '&v=weekly&loading=async&callback=' + CALLBACK;
    s.onerror = () => {
      // Let the next mount try again rather than latching the failure forever.
      pending = null;
      reject(new Error('script-failed'));
    };
    document.head.appendChild(s);
  });
  return pending;
}
